import React, { PropsWithChildren } from "react";

import { onMutation, onResize, size, WrappedEvent } from "@xapp/components";

export interface Dimensions {
    width: number;
    height: number;
}

export interface MeasureProps {
    style?: React.CSSProperties;
    readonly className?: string;
    onMeasure(newDimens: Dimensions): void;
}

interface MeasureState {
    lastMeasure: Dimensions;
}

export class Measure extends React.Component<PropsWithChildren<MeasureProps>, MeasureState> {
    private root: Element;
    private resizeEvent: WrappedEvent;
    private observer: MutationObserver;

    constructor(props: MeasureProps) {
        super(props);

        this.updateDimensions = this.updateDimensions.bind(this);
        this.onMutation = this.onMutation.bind(this);
        this.onRootElement = this.onRootElement.bind(this);

        this.state = {
            lastMeasure: {
                height: -1,
                width: -1
            }
        };
    }

    componentDidMount() {
        this.resizeEvent = onResize(this.updateDimensions);
        this.resizeEvent.register();
        this.updateDimensions();

        this.observer = onMutation(this.root, this.onMutation);
    }

    onMutation(mutations: MutationRecord[], observer: MutationObserver) {
        this.updateDimensions();
    }

    updateDimensions() {
        const { lastMeasure } = this.state;
        const dimensions = this.getDimensions();

        if (lastMeasure.width !== dimensions.width || lastMeasure.height !== dimensions.height) {
            this.props.onMeasure(dimensions);
            this.setState({ lastMeasure: dimensions });
        }
    }

    componentWillUnmount() {
        this.resizeEvent.unregister();
        this.observer.disconnect();
    }

    onRootElement(root: Element) {
        this.root = root;
    }

    getDimensions(): Dimensions {
        // Algorithm taken from https://andylangton.co.uk/blog/development/get-viewportwindow-size-width-and-height-javascript
        // Modified to get around unit tests which don't have half this.
        let width: number, height: number, heightOffset: number;

        if (window) {
            //
            const padding = 16;
            const windowDimens = size();
            const rect = this.root.getBoundingClientRect();
            heightOffset = rect.top;
            width = rect.right - rect.left;
            height = windowDimens.height - heightOffset - padding;
        } else {
            // Unit tests
            width = 200;
            height = 200;
            heightOffset = 0;
        }
        const obj: Dimensions = {
            height: height,
            width: width
        };

        return obj;
    }

    render() {
        return (
            <div style={this.props.style} className={this.props.className} ref={this.onRootElement}>
                {this.props.children}
            </div>
        );
    }
}

export default Measure;
